import { Link, NavLink, useLocation } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import style from "./Header.module.scss";
import { useAppContext } from "../../../lib/AppContext";

const LINKS = [
  { to: "/music", label: "Music" },
  { to: "/sound-design", label: "Sound Design" },
  { to: "/news", label: "News" },
  { to: "/about-me", label: "About me" },
];

const CONTACT = { to: "/contact", label: "Contact" };

const linkClass = ({ isActive }) => (isActive ? `${style.link} ${style.active}` : style.link);

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAdminPage } = useAppContext();
  const location = useLocation();

  // The highlight pill slides to the hovered link, and rests on the current page's link
  const pillRef = useRef(null);
  const [highlight, setHighlight] = useState(null);

  const moveHighlightTo = useCallback((element) => {
    if (!element) return setHighlight(null);
    setHighlight({ left: element.offsetLeft, width: element.offsetWidth });
  }, []);

  const resetHighlight = useCallback(() => {
    moveHighlightTo(pillRef.current?.querySelector(`.${style.active}`));
  }, [moveHighlightTo]);

  useLayoutEffect(() => {
    resetHighlight();
  }, [location.pathname, resetHighlight]);

  // Fonts can load after the first measurement, which shifts the links
  useEffect(() => {
    document.fonts?.ready.then(resetHighlight);
    window.addEventListener("resize", resetHighlight);
    return () => window.removeEventListener("resize", resetHighlight);
  }, [resetHighlight]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Once the page scrolls, the header gets a backdrop so it stays readable over content
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  const scrollToTop = () => window.scrollTo(0, 0);

  return (
    <nav
      className={[style.nav, scrolled && style.scrolled, menuOpen && style.menuOpen]
        .filter(Boolean)
        .join(" ")}
    >
      <Link to="/" className={style.logo} onClick={scrollToTop}>
        <img
          src="https://res.cloudinary.com/dvwkvoqss/image/upload/v1750414929/AnukaLogo_yenkkm.svg"
          alt="The logo of Anuka"
        />
      </Link>

      {!isAdminPage && (
        <>
          <div ref={pillRef} className={style.pill} onMouseLeave={resetHighlight}>
            <span
              className={style.highlight}
              aria-hidden
              style={
                highlight
                  ? {
                      opacity: 1,
                      width: highlight.width,
                      transform: `translateX(${highlight.left}px)`,
                    }
                  : { opacity: 0 }
              }
            />
            <ul>
              {LINKS.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    className={linkClass}
                    onClick={scrollToTop}
                    onMouseEnter={(e) => moveHighlightTo(e.currentTarget)}
                    onFocus={(e) => moveHighlightTo(e.currentTarget)}
                    onBlur={resetHighlight}
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          <NavLink
            to={CONTACT.to}
            className={({ isActive }) => `${style.cta} ${isActive ? style.ctaActive : ""}`}
            onClick={scrollToTop}
          >
            <span>{CONTACT.label}</span>
            <FiArrowUpRight aria-hidden />
          </NavLink>

          <button
            type="button"
            className={style.burger}
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
          >
            <span />
            <span />
          </button>

          <div id="mobile-menu" className={style.mobileMenu} aria-hidden={!menuOpen}>
            {[...LINKS, CONTACT].map((link, index) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={linkClass}
                onClick={scrollToTop}
                tabIndex={menuOpen ? 0 : -1}
                style={{ "--i": index }}
              >
                {link.label}
                <FiArrowUpRight aria-hidden />
              </NavLink>
            ))}
          </div>
        </>
      )}
    </nav>
  );
}
